import { useContext } from "react";
import classNames from "classnames";

import { MapContext } from "../../../../Map";
import KeyboardButton from "../KeyboardButton/KeyboardButton";
import styles from "./SymbolKeyboard.module.scss";
import { KeyboardType } from "../../Keyboard";
import {
  getKeyboardNumericSwitchButtonText,
  getKeyboardSwitchButtonText,
} from "../../utils/keyboardUtils";

interface Props {
  handleClick: (...args: any[]) => void;
  handleBackspace: (...args: any[]) => void;
  handleKeyboardTypeSwitch: (type: KeyboardType) => void;
  handleSearch: () => void;
  languageKey?: string;
  languageSwitchButton?: React.ReactNode;
  showSearchKey?: boolean;
  searchDisabled?: boolean;
}

const SymbolKeyboard: React.FC<Props> = ({
  handleClick,
  handleBackspace,
  handleKeyboardTypeSwitch,
  handleSearch,
  languageKey,
  languageSwitchButton,
  showSearchKey = true,
  searchDisabled = false,
}) => {
  const { controlTheme } = useContext(MapContext);

  return (
    <div className={classNames(styles.container, styles[controlTheme])}>
      <div className={styles.backdrop} />
      <div className={classNames(styles.row, styles.rowOne)}>
        <KeyboardButton type="text" text="[" onClick={handleClick} />
        <KeyboardButton type="text" text="]" onClick={handleClick} />
        <KeyboardButton type="text" text="{" onClick={handleClick} />
        <KeyboardButton type="text" text="}" onClick={handleClick} />
        <KeyboardButton type="text" text="#" onClick={handleClick} />
        <KeyboardButton type="text" text="%" onClick={handleClick} />
        <KeyboardButton type="text" text="^" onClick={handleClick} />
        <KeyboardButton type="text" text="*" onClick={handleClick} />
        <KeyboardButton type="text" text="+" onClick={handleClick} />
        <KeyboardButton type="text" text="=" onClick={handleClick} />
      </div>

      <div className={classNames(styles.row, styles.rowTwo)}>
        <KeyboardButton type="text" text="_" onClick={handleClick} />
        <KeyboardButton type="text" text="\" onClick={handleClick} />
        <KeyboardButton type="text" text="|" onClick={handleClick} />
        <KeyboardButton type="text" text="~" onClick={handleClick} />
        <KeyboardButton type="text" text="<" onClick={handleClick} />
        <KeyboardButton type="text" text=">" onClick={handleClick} />
        <KeyboardButton type="text" text="€" onClick={handleClick} />
        <KeyboardButton type="text" text="$" onClick={handleClick} />
        <KeyboardButton type="text" text="¥" onClick={handleClick} />
        <KeyboardButton type="text" text="•" onClick={handleClick} />
      </div>

      <div className={classNames(styles.row, styles.rowThree)}>
        <KeyboardButton
          dataQA="symbol-switch"
          type="text"
          text={getKeyboardNumericSwitchButtonText(languageKey)}
          onClick={() => handleKeyboardTypeSwitch("numeric")}
          className={styles.symbolSwitchKey}
        />
        <div className={styles.rowThreeKeys}>
          <KeyboardButton type="text" text="." onClick={handleClick} />
          <KeyboardButton type="text" text="," onClick={handleClick} />
          <KeyboardButton type="text" text="?" onClick={handleClick} />
          <KeyboardButton type="text" text="!" onClick={handleClick} />
          <KeyboardButton type="text" text="'" onClick={handleClick} />
        </div>
        <KeyboardButton
          dataQA="backspace"
          type="icon"
          icon="backspace"
          value=""
          onClick={handleBackspace}
          className={classNames(styles.backspaceKey, {
            [styles.rtlIcon]: languageKey === "ar-SA",
          })}
        />
      </div>

      <div className={classNames(styles.row, styles.rowFour)}>
        <div className={classNames(styles.column, styles.columnOne)}>
          <KeyboardButton
            dataQA="numberic-switch"
            type="text"
            text={getKeyboardSwitchButtonText(languageKey)}
            onClick={
              handleKeyboardTypeSwitch
                ? () => handleKeyboardTypeSwitch("text")
                : handleClick
            }
            className={styles.numericSwitchKey}
          />
          {languageSwitchButton && languageSwitchButton}
        </div>
        <div className={classNames(styles.column, styles.columnTwo)}>
          <KeyboardButton
            dataQA="space"
            type="text"
            text=""
            onClick={() => handleClick({ currentTarget: { name: " " } })}
            className={styles.spaceKey}
            verticalAlign={false}
          />
          <KeyboardButton
            dataQA="search"
            type="icon"
            icon="search"
            value=""
            onClick={showSearchKey ? handleSearch : () => {}}
            className={classNames(styles.searchKey, {
              [styles.searchKeyHidden]: !showSearchKey,
            })}
            disabled={!showSearchKey || searchDisabled}
          />
        </div>
      </div>
    </div>
  );
};

export default SymbolKeyboard;
